/**
 * Groups.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * All group-related UI in one module. Data is fetched once at the top-level
 * <Groups> component and passed down — no child makes its own Firestore calls.
 *
 * Auth levels (read from Firebase Auth custom claims via useUserLevel):
 *   super_admin → full control: create, modify, delete any group
 *   user        → read-only: view their own group only
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection, getDocs, doc, getDoc,
  addDoc, updateDoc, deleteDoc, arrayRemove,
  query, where, Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useUserLevel, AdminLevel } from '../context/admin';

// ─── Types ────────────────────────────────────────────────────────────────────

interface FormOption {
  id: number;
  name: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  groupId: string | null;
  access: Record<string, boolean>;
  userSequences?: Record<string, number>;
  forms?: Array<{ form_id: number; completed: boolean }>;
}

interface Group {
  id: string;
  groupId: string;
  recoveryPlan: string;
  Members: string[];
  password: string;
  access: Record<string, boolean>;
  permissions: AppPermissions;
  backMovements: Record<string, boolean>;
  sequences?: Record<ProductCode, number>;
  forms?: Array<{ form_id: number; completed: boolean }>;
}

interface RecoveryPlan {
  id: string;
  name: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PRODUCT_CODES = ['VM1MOAABLE', 'VM2MOBABLE', 'VM4MOAABLE', 'VM6MOAABLE'] as const;
type ProductCode = typeof PRODUCT_CODES[number];

const DEFAULT_PRODUCT_INDEXES: Record<ProductCode, number> = {
  VM1MOAABLE: 1, VM2MOBABLE: 1, VM4MOAABLE: 1, VM6MOAABLE: 1,
};
const DEFAULT_SEQUENCE_COUNTS: Record<ProductCode, number> = {
  VM1MOAABLE: 1, VM2MOBABLE: 1, VM4MOAABLE: 1, VM6MOAABLE: 1,
};

// Per-user app switches. The app reads these from the user doc only; a group
// stores its own copy and pushes it onto every member's user doc on save.
// Missing on a user doc means allowed.
interface AppPermissions {
  treatmentPainAllowed: boolean;
  accountSettingsAllowed: boolean;
}

const DEFAULT_PERMISSIONS: AppPermissions = {
  treatmentPainAllowed: true,
  accountSettingsAllowed: true,
};

const PERMISSION_LABELS: Record<keyof AppPermissions, string> = {
  treatmentPainAllowed: 'Pre/post treatment pain sliders',
  accountSettingsAllowed: 'Account Settings in Profile',
};

// Groups saved before this switched to user-doc permissions stored the pain
// flag as `treatmentPainEnabled`.
const readGroupPermissions = (data: Record<string, any>): AppPermissions => ({
  treatmentPainAllowed:
    data.treatmentPainAllowed ?? data.treatmentPainEnabled ?? DEFAULT_PERMISSIONS.treatmentPainAllowed,
  accountSettingsAllowed:
    data.accountSettingsAllowed ?? DEFAULT_PERMISSIONS.accountSettingsAllowed,
});

// Back assessment movements in app order; keys of the user doc's
// `backAssessmentMovements` map. A missing key means the movement is included.
export const BACK_MOVEMENTS = [
  'Flexion', 'Extension', 'Left Rotation',
  'Right Rotation', 'Left Lateral Bend', 'Right Lateral Bend',
] as const;

const ALL_BACK_MOVEMENTS: Record<string, boolean> =
  Object.fromEntries(BACK_MOVEMENTS.map(m => [m, true]));

// Mirrors the app's widget catalog and its default `access` map. A widget
// missing from a user's `access` map is shown by the app, so every widget
// needs to be listed here to be switchable.
export const DEFAULT_FEATURES: Record<string, boolean> = {
  'Analytics': false,
  'Arm Assessment': false,
  'Back Assessment': false,
  'Back Recover': false,
  'Forms': false,           
  'Frequency': true,
  'IMU': true,
  'Knee Assessment': false,
  'Manufacture': false,
  'Power': true,
  'Research Data': true,
  'Schedule': false,
  'Sit to Stand': false,   
  'Treatment': false,
  'Wearables': false,
};

// ─── Shared data shape passed to all sub-views ────────────────────────────────

interface SharedData {
  users: User[];
  groups: Group[];
  recoveryPlans: RecoveryPlan[];
  sequenceCounts: Record<ProductCode, number>;
  availableForms: FormOption[];
}

// ─── Shared UI primitives ─────────────────────────────────────────────────────

const InfoBox: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-blue-50 border border-blue-300 text-blue-700 p-3 rounded-md mb-4 text-sm space-y-1">
    {children}
  </div>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-lg font-medium text-gray-700 mb-2">{children}</label>
);

const ModalShell: React.FC<{ title: string; onClose: () => void; children: React.ReactNode }> = ({
  title, onClose, children,
}) => (
  <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/50">
    <div className="bg-white h-5/6 w-11/12 lg:w-3/4 xl:w-2/3 p-8 rounded-3xl shadow-2xl relative overflow-auto">
      <button
        className="btn btn-sm btn-circle absolute right-4 top-4"
        onClick={onClose}
        aria-label="Close"
      >✕</button>
      <h2 className="text-3xl font-semibold text-gray-800 mb-8">{title}</h2>
      {children}
    </div>
  </div>
);

// ─── Shared form sections ─────────────────────────────────────────────────────

const FeatureAccessSection: React.FC<{
  featureList: Record<string, boolean>;
  onChange: (feature: string) => void;
  readOnly?: boolean;
}> = ({ featureList, onChange, readOnly = false }) => {
  // Every known widget, plus any extra keys already stored; a missing key is
  // allowed in the app, so show it ticked.
  const sorted = useMemo(
    () => Array.from(new Set([...Object.keys(DEFAULT_FEATURES), ...Object.keys(featureList)]))
      .map(f => [f, featureList[f] !== false] as const)
      .sort(([a], [b]) => a.localeCompare(b)),
    [featureList]
  );
  return (
    <section>
      <h3 className="text-xl font-medium text-gray-800 mb-3">Feature Access</h3>
      <InfoBox>
        While users are members of this group, their individual feature access is overridden by the group's settings.
      </InfoBox>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {sorted.map(([feature, enabled]) => (
          <label
            key={feature}
            className={`flex items-center gap-2 select-none ${readOnly ? 'opacity-60' : 'cursor-pointer'}`}
          >
            <input
              type="checkbox"
              checked={enabled}
              onChange={() => !readOnly && onChange(feature)}
              className="checkbox checkbox-primary"
              disabled={readOnly}
            />
            <span className="text-sm text-gray-700">{feature}</span>
          </label>
        ))}
      </div>
    </section>
  );
};

const AppPermissionsSection: React.FC<{
  permissions: AppPermissions;
  onChange: (permissions: AppPermissions) => void;
  readOnly?: boolean;
}> = ({ permissions, onChange, readOnly = false }) => (
  <section>
    <h3 className="text-xl font-medium text-gray-800 mb-3">App Permissions</h3>
    <InfoBox>
      Saving the group applies these to every member. They can still be changed per user from the user's profile afterwards.
    </InfoBox>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {(Object.keys(PERMISSION_LABELS) as (keyof AppPermissions)[]).map(key => (
        <label
          key={key}
          className={`flex items-center gap-2 select-none ${readOnly ? 'opacity-60' : 'cursor-pointer'}`}
        >
          <input
            type="checkbox"
            checked={permissions[key]}
            onChange={() => !readOnly && onChange({ ...permissions, [key]: !permissions[key] })}
            className="checkbox checkbox-primary"
            disabled={readOnly}
          />
          <span className="text-sm text-gray-700">{PERMISSION_LABELS[key]}</span>
        </label>
      ))}
    </div>
  </section>
);

const BackMovementsSection: React.FC<{
  movements: Record<string, boolean>;
  onChange: (movements: Record<string, boolean>) => void;
  readOnly?: boolean;
}> = ({ movements, onChange, readOnly = false }) => (
  <section>
    <h3 className="text-xl font-medium text-gray-800 mb-3">Back Assessment Movements</h3>
    <InfoBox>
      The movements members perform in the back assessment, in this order. The mobility score averages only the movements performed.
    </InfoBox>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {BACK_MOVEMENTS.map(m => {
        const included = movements[m] !== false;
        return (
          <label
            key={m}
            className={`flex items-center gap-2 select-none ${readOnly ? 'opacity-60' : 'cursor-pointer'}`}
          >
            <input
              type="checkbox"
              checked={included}
              onChange={() => !readOnly && onChange({ ...movements, [m]: !included })}
              className="checkbox checkbox-primary"
              disabled={readOnly}
            />
            <span className="text-sm text-gray-700">{m}</span>
          </label>
        );
      })}
    </div>
  </section>
);

const SequenceSection: React.FC<{
  productIndexes: Record<ProductCode, number>;
  sequenceCounts: Record<ProductCode, number>;
  onChange: (code: ProductCode, index: number) => void;
}> = ({ productIndexes, sequenceCounts, onChange }) => (
  <section>
    <h3 className="text-xl font-medium text-gray-800 mb-3">Assign Sequences</h3>
    <InfoBox>
      Choose a sequence index per Myovolt product. These will be applied to all group members.
    </InfoBox>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {PRODUCT_CODES.map(code => (
        <div key={code}>
          <label className="block text-sm font-medium text-gray-600 mb-1">{code}</label>
          <select
            value={productIndexes[code]}
            onChange={e => onChange(code, parseInt(e.target.value))}
            className="select select-bordered w-full"
          >
            {Array.from({ length: sequenceCounts[code] || 1 }, (_, i) => (
              <option key={i + 1} value={i + 1}>{i + 1}</option>
            ))}
          </select>
        </div>
      ))}
    </div>
  </section>
);

const FormsSection: React.FC<{
  availableForms: FormOption[];
  selectedFormIds: Set<number>;
  onChange: (id: number) => void;
}> = ({ availableForms, selectedFormIds, onChange }) => (
  <section>
    <h3 className="text-xl font-medium text-gray-800 mb-3">Assign Forms</h3>
    <InfoBox>
      Selected forms will be assigned to all group members when they join.
    </InfoBox>
    {availableForms.length === 0 ? (
      <p className="text-sm text-gray-400">No forms available. Create forms from the Forms menu.</p>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {availableForms.map(form => (
          <label key={form.id} className="flex items-center gap-3 cursor-pointer bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 hover:bg-gray-100 transition-colors">
            <input
              type="checkbox"
              checked={selectedFormIds.has(form.id)}
              onChange={() => onChange(form.id)}
              className="checkbox checkbox-primary"
            />
            <span className="text-sm text-gray-700">
              <span className="text-gray-400 mr-1">#{form.id}</span>
              {form.name}
            </span>
          </label>
        ))}
      </div>
    )}
  </section>
);

const MemberList: React.FC<{
  users: User[];
  searchQuery: string;
  onSearchChange: (q: string) => void;
  selectedMembers: Set<string>;
  onToggleMember: (id: string) => void;
  showGroupWarning?: boolean;
  currentGroupId?: string;
}> = ({
  users, searchQuery, onSearchChange,
  selectedMembers, onToggleMember,
  showGroupWarning = false, currentGroupId,
}) => (
  <section>
    <h3 className="text-xl font-medium text-gray-800 mb-3">Assign Members</h3>
    {showGroupWarning && (
      <InfoBox>
        <p>All users are shown — users in other groups are highlighted.</p>
      </InfoBox>
    )}

    <input
      type="text"
      placeholder="Search users by name or email"
      value={searchQuery}
      onChange={e => onSearchChange(e.target.value)}
      className="input input-bordered w-full mb-3"
    />

    <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-xl divide-y divide-gray-100">
      {users.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-6">No users found.</p>
      ) : (
        users.map(user => (
          <div
            key={user.id}
            className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
          >
            <div>
              <p className="text-sm font-medium text-gray-900">{user.name}</p>
              <p className="text-sm text-gray-500">{user.email}</p>
              {showGroupWarning && user.groupId && user.groupId !== currentGroupId && (
                <span className="text-xs text-amber-600 font-medium">
                  Currently in: {user.groupId}
                </span>
              )}
            </div>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Member</span>
              <input
                type="checkbox"
                checked={selectedMembers.has(user.id)}
                onChange={() => onToggleMember(user.id)}
                className="checkbox checkbox-primary checkbox-sm"
              />
            </label>
          </div>
        ))
      )}
    </div>

    {selectedMembers.size > 0 && (
      <div className="mt-3 text-sm text-gray-500">
        Members: <strong className="text-gray-700">{selectedMembers.size}</strong>
      </div>
    )}
  </section>
);

// ═════════════════════════════════════════════════════════════════════════════
// SUB-VIEWS
// ═════════════════════════════════════════════════════════════════════════════

// ─── SuperAdmin: Create Group ─────────────────────────────────────────────────

const CreateGroupAdmin: React.FC<{ data: SharedData; onClose: () => void }> = ({ data, onClose }) => {
  const { users, groups, recoveryPlans, sequenceCounts, availableForms } = data;

  const [groupName, setGroupName]         = useState('');
  const [recoveryPlan, setRecoveryPlan]   = useState('');
  const [password, setPassword]           = useState('');
  const [featureList, setFeatureList]     = useState<Record<string, boolean>>(DEFAULT_FEATURES);
  const [permissions, setPermissions]     = useState<AppPermissions>(DEFAULT_PERMISSIONS);
  const [backMovements, setBackMovements] = useState<Record<string, boolean>>(ALL_BACK_MOVEMENTS);
  const [productIndexes, setProductIndexes] = useState<Record<ProductCode, number>>(DEFAULT_PRODUCT_INDEXES);
  const [selectedFormIds, setSelectedFormIds] = useState<Set<number>>(new Set());
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery]     = useState('');
  const [loading, setLoading]             = useState(false);

  const handleFeatureToggle = useCallback((f: string) =>
    setFeatureList(prev => ({ ...prev, [f]: prev[f] === false })), []);

  const handleProductIndexChange = useCallback((code: ProductCode, index: number) =>
    setProductIndexes(prev => ({ ...prev, [code]: index })), []);

  const handleFormToggle = useCallback((id: number) =>
    setSelectedFormIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; }), []);

  const handleToggleMember = useCallback((userId: string) => {
    setSelectedMembers(prev => {
      const s = new Set(prev);
      s.has(userId) ? s.delete(userId) : s.add(userId);
      return s;
    });
  }, []);

  const handleCreate = useCallback(async () => {
    if (!groupName.trim()) { alert('Please enter a group name.'); return; }
    if (groups.some(g => g.groupId === groupName.trim())) {
      alert('A group with this name already exists.'); return;
    }

    setLoading(true);
    try {
      const memberEmails = Array.from(selectedMembers)
        .map(id => users.find(u => u.id === id)?.email)
        .filter(Boolean) as string[];

      const assignedForms = Array.from(selectedFormIds).map(id => ({ form_id: id, completed: false }));

      await addDoc(collection(db, 'groups'), {
        groupId: groupName.trim(),
        recoveryPlan,
        password,
        Members: memberEmails,
        access: featureList,
        ...permissions,
        backAssessmentMovements: backMovements,
        sequences: productIndexes,
        forms: assignedForms,
      });

      await Promise.all(
        users.filter(u => selectedMembers.has(u.id)).map(u =>
          updateDoc(doc(db, 'users', u.id), {
            groupId: groupName.trim(),
            recoveryPlan,
            access: featureList,
            ...permissions,
            backAssessmentMovements: backMovements,
            userSequences: productIndexes,
            forms: assignedForms,
          })
        )
      );

      onClose();
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert('Error creating group. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [groupName, selectedMembers, groups, users, recoveryPlan, password, featureList, permissions, backMovements, productIndexes, selectedFormIds, onClose]);

  // Only show ungrouped users for new group creation
  const filteredUsers = useMemo(() =>
    users.filter(u =>
      !u.groupId &&
      (u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
       u.email.toLowerCase().includes(searchQuery.toLowerCase()))
    ), [users, searchQuery]);

  return (
    <ModalShell title="Create New Group" onClose={onClose}>
      <div className="space-y-8">
        <section>
          <SectionLabel>Group Name</SectionLabel>
          <InfoBox>The group's name must be unique as it is used to identify the group.</InfoBox>
          <input type="text" value={groupName} onChange={e => setGroupName(e.target.value)}
            className="input input-bordered w-full" placeholder="Enter group name" />
        </section>

        <section>
          <SectionLabel>Recovery Plan</SectionLabel>
          <InfoBox>The recovery plan that will be assigned to all members of the group.</InfoBox>
          <select value={recoveryPlan} onChange={e => setRecoveryPlan(e.target.value)}
            className="select select-bordered w-full">
            <option value="" disabled>Select a recovery plan</option>
            {recoveryPlans.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
          </select>
        </section>

        <section>
          <SectionLabel>Group Password</SectionLabel>
          <InfoBox>The code users enter alongside the group name to join via the app.</InfoBox>
          <input type="text" value={password} onChange={e => setPassword(e.target.value)}
            className="input input-bordered w-full" placeholder="Enter group password" />
        </section>

        <FeatureAccessSection featureList={featureList} onChange={handleFeatureToggle} />

        <AppPermissionsSection permissions={permissions} onChange={setPermissions} />

        <BackMovementsSection movements={backMovements} onChange={setBackMovements} />

        <SequenceSection
          productIndexes={productIndexes}
          sequenceCounts={sequenceCounts}
          onChange={handleProductIndexChange}
        />

        <FormsSection
          availableForms={availableForms}
          selectedFormIds={selectedFormIds}
          onChange={handleFormToggle}
        />

        <MemberList
          users={filteredUsers}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          selectedMembers={selectedMembers}
          onToggleMember={handleToggleMember}
        />

        <div className="flex justify-end pt-2">
          <button
            onClick={handleCreate}
            className="btn btn-primary px-8"
            disabled={loading || !groupName.trim()}
          >
            {loading ? <span className="loading loading-spinner loading-sm" /> : 'Create Group'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

// ─── SuperAdmin: Modify / Delete any Group ────────────────────────────────────

const ModifyGroupAdmin: React.FC<{ data: SharedData; group: Group; onClose: () => void }> = ({ data, group, onClose }) => {
  const { users, recoveryPlans, sequenceCounts, availableForms } = data;

  const [groupName, setGroupName]         = useState(group.groupId);
  const [recoveryPlan, setRecoveryPlan]   = useState(group.recoveryPlan);
  const [password, setPassword]           = useState(group.password);
  const [featureList, setFeatureList]     = useState<Record<string, boolean>>(group.access || DEFAULT_FEATURES);
  const [permissions, setPermissions]     = useState<AppPermissions>(group.permissions);
  const [backMovements, setBackMovements] = useState<Record<string, boolean>>(group.backMovements);
  const [productIndexes, setProductIndexes] = useState<Record<ProductCode, number>>(group.sequences ?? DEFAULT_PRODUCT_INDEXES);
  const [selectedFormIds, setSelectedFormIds] = useState<Set<number>>(
    new Set((group.forms ?? []).map((f: { form_id: number }) => f.form_id))
  );
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(
    new Set(users.filter(u => group.Members.includes(u.email)).map(u => u.id))
  );
  const [searchQuery, setSearchQuery]     = useState('');
  const [loading, setLoading]             = useState(false);

  const handleFeatureToggle = useCallback((f: string) =>
    setFeatureList(prev => ({ ...prev, [f]: prev[f] === false })), []);

  const handleProductIndexChange = useCallback((code: ProductCode, index: number) =>
    setProductIndexes(prev => ({ ...prev, [code]: index })), []);

  const handleFormToggle = useCallback((id: number) =>
    setSelectedFormIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; }), []);

  const handleToggleMember = useCallback((userId: string) => {
    setSelectedMembers(prev => {
      const s = new Set(prev);
      s.has(userId) ? s.delete(userId) : s.add(userId);
      return s;
    });
  }, []);

  const handleUpdate = useCallback(async () => {
    setLoading(true);
    try {
      const memberEmails = Array.from(selectedMembers)
        .map(id => users.find(u => u.id === id)?.email)
        .filter(Boolean) as string[];

      const assignedForms = Array.from(selectedFormIds).map(id => ({ form_id: id, completed: false }));

      await updateDoc(doc(db, 'groups', group.id), {
        groupId: groupName,
        recoveryPlan,
        Members: memberEmails,
        password,
        access: featureList,
        ...permissions,
        backAssessmentMovements: backMovements,
        sequences: productIndexes,
        forms: assignedForms,
      });

      // Members joining from a different group must be removed from that
      // group's Members array, or they'd end up listed in both.
      const previousGroupEmails = new Map<string, string[]>();
      users.forEach(u => {
        if (selectedMembers.has(u.id) && u.groupId && u.groupId !== group.groupId) {
          const prevGroup = data.groups.find(g => g.groupId === u.groupId);
          if (prevGroup) {
            previousGroupEmails.set(prevGroup.id, [...(previousGroupEmails.get(prevGroup.id) ?? []), u.email]);
          }
        }
      });

      await Promise.all(Array.from(previousGroupEmails.entries()).map(([prevGroupDocId, emails]) =>
        updateDoc(doc(db, 'groups', prevGroupDocId), { Members: arrayRemove(...emails) })
      ));

      await Promise.all(users.map(u => {
        const ref = doc(db, 'users', u.id);
        if (selectedMembers.has(u.id)) {
          return updateDoc(ref, { groupId: groupName, recoveryPlan, access: featureList, ...permissions, backAssessmentMovements: backMovements, userSequences: productIndexes, forms: assignedForms });
        }
        if (u.groupId === group.groupId) {
          return updateDoc(ref, { groupId: null, recoveryPlan: null, access: DEFAULT_FEATURES, ...DEFAULT_PERMISSIONS, backAssessmentMovements: ALL_BACK_MOVEMENTS, userSequences: DEFAULT_PRODUCT_INDEXES, forms: [] });
        }
        return Promise.resolve();
      }));

      onClose();
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert('Error updating group. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [group, selectedMembers, users, groupName, recoveryPlan, password, featureList, permissions, backMovements, productIndexes, selectedFormIds, onClose, data.groups]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm(`Delete "${group.groupId}"? This cannot be undone.`)) return;

    setLoading(true);
    try {
      await Promise.all(
        users.filter(u => group.Members.includes(u.email)).map(u =>
          updateDoc(doc(db, 'users', u.id), {
            groupId: null,
            recoveryPlan: null,
            recoveryPlanDate: null,
            access: DEFAULT_FEATURES,
            ...DEFAULT_PERMISSIONS,
            backAssessmentMovements: ALL_BACK_MOVEMENTS,
            userSequences: DEFAULT_PRODUCT_INDEXES,
          })
        )
      );
      await deleteDoc(doc(db, 'groups', group.id));
      onClose();
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert('Error deleting group. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [group, users, onClose]);

  const filteredUsers = useMemo(() =>
    users.filter(u =>
      u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase())
    ), [users, searchQuery]);

  return (
    <ModalShell title={`Modify "${group.groupId}"`} onClose={onClose}>
      <div className="space-y-8">
        <section>
          <SectionLabel>Group Name</SectionLabel>
          <InfoBox>The group's name must be unique as it is used to identify the group.</InfoBox>
          <input type="text" value={groupName} onChange={e => setGroupName(e.target.value)}
            className="input input-bordered w-full" />
        </section>

        <section>
          <SectionLabel>Recovery Plan</SectionLabel>
          <InfoBox>The recovery plan assigned to all members of the group.</InfoBox>
          <select value={recoveryPlan} onChange={e => setRecoveryPlan(e.target.value)}
            className="select select-bordered w-full">
            <option value="" disabled>Select a recovery plan</option>
            {recoveryPlans.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
          </select>
        </section>

        <section>
          <SectionLabel>Group Password</SectionLabel>
          <InfoBox>The code users enter alongside the group name to join via the app.</InfoBox>
          <input type="text" value={password} onChange={e => setPassword(e.target.value)}
            className="input input-bordered w-full" />
        </section>

        <FeatureAccessSection featureList={featureList} onChange={handleFeatureToggle} />

        <AppPermissionsSection permissions={permissions} onChange={setPermissions} />

        <BackMovementsSection movements={backMovements} onChange={setBackMovements} />

        <SequenceSection
          productIndexes={productIndexes}
          sequenceCounts={sequenceCounts}
          onChange={handleProductIndexChange}
        />

        <FormsSection
          availableForms={availableForms}
          selectedFormIds={selectedFormIds}
          onChange={handleFormToggle}
        />

        <MemberList
          users={filteredUsers}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          selectedMembers={selectedMembers}
          onToggleMember={handleToggleMember}
          showGroupWarning
          currentGroupId={group.groupId}
        />

        <div className="flex items-center justify-between pt-2 border-t border-gray-100">
          <button onClick={handleDelete} className="btn btn-error btn-outline" disabled={loading}>
            {loading ? <span className="loading loading-spinner loading-sm" /> : 'Delete Group'}
          </button>
          <button
            onClick={handleUpdate}
            className="btn btn-primary px-8"
            disabled={loading || !groupName.trim()}
          >
            {loading ? <span className="loading loading-spinner loading-sm" /> : 'Update Group'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
};

// ─── User: View their own Group (read-only) ───────────────────────────────────

const ViewMyGroup: React.FC<{
  data: SharedData;
  groupId: string;
  onClose: () => void;
}> = ({ data, groupId, onClose }) => {
  const { users, groups } = data;

  const group = useMemo(() =>
    groups.find(g => g.groupId === groupId) || null,
    [groups, groupId]
  );

  const [searchQuery, setSearchQuery] = useState('');

  const members = useMemo(() => {
    if (!group) return [];
    return users.filter(u =>
      group.Members.includes(u.email) &&
      (u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
       u.email.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [group, users, searchQuery]);

  return (
    <ModalShell title="My Group" onClose={onClose}>
      {!group ? (
        <p className="text-gray-500">Group not found.</p>
      ) : (
        <div className="space-y-8">
          <section>
            <SectionLabel>Group Name</SectionLabel>
            <input type="text" value={group.groupId} readOnly
              className="input input-bordered w-full bg-gray-50" />
          </section>

          <section>
            <SectionLabel>Recovery Plan</SectionLabel>
            <input type="text" value={group.recoveryPlan} readOnly
              className="input input-bordered w-full bg-gray-50" />
          </section>

          <FeatureAccessSection
            featureList={group.access || DEFAULT_FEATURES}
            onChange={() => {}}
            readOnly
          />

          <AppPermissionsSection
            permissions={group.permissions}
            onChange={() => {}}
            readOnly
          />

          <BackMovementsSection
            movements={group.backMovements}
            onChange={() => {}}
            readOnly
          />

          <section>
            <h3 className="text-xl font-medium text-gray-800 mb-3">
              Members <span className="text-base font-normal text-gray-400">({group.Members.length})</span>
            </h3>
            <input
              type="text"
              placeholder="Search members"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="input input-bordered w-full mb-3"
            />
            <div className="max-h-72 overflow-y-auto border border-gray-200 rounded-xl divide-y divide-gray-100">
              {members.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No members found.</p>
              ) : (
                members.map(u => (
                  <div key={u.id} className="flex items-center justify-between px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{u.name}</p>
                      <p className="text-sm text-gray-500">{u.email}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </section>
        </div>
      )}
    </ModalShell>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// GROUP VIEW — overview grid with activity analytics
// ═════════════════════════════════════════════════════════════════════════════

// ─── Types ───────────────────────────────────────────────────────────────────

interface MemberActivity {
  userId: string;
  name: string;
  email: string;
  treatmentCount: number;
  totalMinutes: number;
  lastSeen: Date | null;
}

interface GroupStats {
  group: Group;
  memberCount: number;
  treatmentCount30d: number;
  totalMinutes30d: number;
  activeMembers30d: number;
  lastActivity: Date | null;
  members: MemberActivity[];
  activityLevel: 'very-active' | 'active' | 'quiet' | 'dormant' | 'new';
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const THIRTY_DAYS_AGO = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

function classifyActivity(treatments30d: number, memberCount: number): GroupStats['activityLevel'] {
  if (memberCount === 0) return 'new';
  const rate = treatments30d / Math.max(memberCount, 1);
  if (rate >= 10) return 'very-active';
  if (rate >= 4)  return 'active';
  if (rate >= 1)  return 'quiet';
  if (treatments30d > 0) return 'dormant';
  return 'dormant';
}

const ACTIVITY_META: Record<GroupStats['activityLevel'], { label: string; dot: string; badge: string }> = {
  'very-active': { label: 'Very Active', dot: 'bg-emerald-500', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  'active':      { label: 'Active',      dot: 'bg-green-400',   badge: 'bg-green-50 text-green-700 border-green-200'       },
  'quiet':       { label: 'Quiet',       dot: 'bg-amber-400',   badge: 'bg-amber-50 text-amber-700 border-amber-200'       },
  'dormant':     { label: 'Dormant',     dot: 'bg-gray-300',    badge: 'bg-gray-100 text-gray-500 border-gray-200'         },
  'new':         { label: 'New',         dot: 'bg-blue-400',    badge: 'bg-blue-50 text-blue-700 border-blue-200'          },
};

function fmtDate(d: Date | null): string {
  if (!d) return 'No activity';
  const diff = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7)  return `${diff}d ago`;
  if (diff < 30) return `${Math.floor(diff / 7)}w ago`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Avatar initials ──────────────────────────────────────────────────────────

const AVATAR_PALETTE = [
  'bg-violet-500', 'bg-blue-500', 'bg-teal-500', 'bg-emerald-500',
  'bg-amber-500',  'bg-rose-500', 'bg-pink-500',  'bg-indigo-500',
];

const InitialAvatar: React.FC<{ name: string; size?: 'sm' | 'md' }> = ({ name, size = 'md' }) => {
  const initials = name.split(' ').slice(0, 2).map(w => w[0] ?? '').join('').toUpperCase() || '?';
  const colour = AVATAR_PALETTE[name.charCodeAt(0) % AVATAR_PALETTE.length];
  const sz = size === 'sm' ? 'w-7 h-7 text-xs' : 'w-9 h-9 text-sm';
  return (
    <span className={`${sz} ${colour} rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0`}>
      {initials}
    </span>
  );
};

// ─── Spark bar ────────────────────────────────────────────────────────────────

const SparkBar: React.FC<{ value: number; max: number; colour?: string }> = ({
  value, max, colour = 'bg-MyovoltPurple',
}) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden flex-1">
      <div className={`h-full ${colour} rounded-full transition-all`} style={{ width: `${pct}%` }} />
    </div>
  );
};

// ─── Stat chip ────────────────────────────────────────────────────────────────

const StatChip: React.FC<{ icon: React.ReactNode; value: string | number; label: string }> = ({
  icon, value, label,
}) => (
  <div className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2 min-w-0">
    <span className="text-gray-400 flex-shrink-0">{icon}</span>
    <div className="min-w-0">
      <p className="text-sm font-semibold text-gray-800 leading-none">{value}</p>
      <p className="text-xs text-gray-400 mt-0.5 truncate">{label}</p>
    </div>
  </div>
);

// ─── Expanded group detail panel ──────────────────────────────────────────────

const GroupDetailPanel: React.FC<{ stats: GroupStats; onClose: () => void; onModify: () => void }> = ({ stats, onClose, onModify }) => {
  const { group, members, treatmentCount30d, totalMinutes30d, lastActivity, activityLevel } = stats;
  const meta = ACTIVITY_META[activityLevel];
  const maxTreatments = Math.max(...members.map(m => m.treatmentCount), 1);
  const [memberSearch, setMemberSearch] = React.useState('');

  const filteredMembers = useMemo(() =>
    members
      .filter(m => m.name.toLowerCase().includes(memberSearch.toLowerCase()) ||
                   m.email.toLowerCase().includes(memberSearch.toLowerCase()))
      .sort((a, b) => b.treatmentCount - a.treatmentCount),
    [members, memberSearch]
  );

  const enabledFeatures = Object.entries(group.access || {})
    .filter(([, v]) => v)
    .map(([k]) => k)
    .sort();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-3xl max-h-[90vh] rounded-3xl shadow-2xl flex flex-col overflow-hidden">

        {/* ── Header ── */}
        <div className="px-7 pt-7 pb-5 border-b border-gray-100 flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <InitialAvatar name={group.groupId} size="md" />
            <div>
              <h2 className="text-xl font-bold text-gray-900 leading-tight">{group.groupId}</h2>
              <p className="text-sm text-gray-500 mt-0.5">Plan: {group.recoveryPlan || '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${meta.badge}`}>
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${meta.dot} mr-1.5`} />
              {meta.label}
            </span>
            <button className="btn btn-sm btn-outline" onClick={onModify}>Modify</button>
            <button className="btn btn-sm btn-circle btn-ghost" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-7 py-5 space-y-6">

          {/* ── Summary stats ── */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <StatChip
              icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
              value={stats.memberCount}
              label="Members"
            />
            <StatChip
              icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>}
              value={treatmentCount30d}
              label="Treatments (30d)"
            />
            <StatChip
              icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
              value={`${Math.round(totalMinutes30d / 60)}h ${Math.round(totalMinutes30d % 60)}m`}
              label="Treatment time (30d)"
            />
            <StatChip
              icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
              value={fmtDate(lastActivity)}
              label="Last activity"
            />
          </div>

          {/* ── Enabled features ── */}
          {enabledFeatures.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">Enabled features</p>
              <div className="flex flex-wrap gap-1.5">
                {enabledFeatures.map(f => (
                  <span key={f} className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                    {f}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ── Member activity table ── */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Member activity — last 30 days
              </p>
              <span className="text-xs text-gray-400">{stats.activeMembers30d} active</span>
            </div>

            <input
              type="text"
              placeholder="Search members…"
              value={memberSearch}
              onChange={e => setMemberSearch(e.target.value)}
              className="input input-bordered input-sm w-full mb-3"
            />

            <div className="divide-y divide-gray-100 rounded-2xl border border-gray-100 overflow-hidden">
              {filteredMembers.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No members found.</p>
              ) : (
                filteredMembers.map(m => {
                  const isActive = m.treatmentCount > 0;
                  return (
                    <div key={m.userId} className="flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 transition-colors">
                      <InitialAvatar name={m.name} size="sm" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{m.name}</p>
                        <p className="text-xs text-gray-400 truncate">{m.email}</p>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0 w-48">
                        <SparkBar value={m.treatmentCount} max={maxTreatments} />
                        <span className="text-xs text-gray-500 w-16 text-right tabular-nums">
                          {m.treatmentCount} tx
                        </span>
                      </div>
                      <span className="text-xs text-gray-400 w-20 text-right tabular-nums flex-shrink-0">
                        {fmtDate(m.lastSeen)}
                      </span>
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isActive ? 'bg-emerald-400' : 'bg-gray-200'}`} />
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Group card (grid tile) ───────────────────────────────────────────────────

const GroupCard: React.FC<{ stats: GroupStats; onClick: () => void }> = ({ stats, onClick }) => {
  const { group, memberCount, treatmentCount30d, totalMinutes30d, lastActivity, activityLevel, activeMembers30d } = stats;
  const meta = ACTIVITY_META[activityLevel];
  const hrs  = Math.floor(totalMinutes30d / 60);
  const mins = Math.round(totalMinutes30d % 60);

  return (
    <button
      onClick={onClick}
      className="group text-left bg-white border border-gray-200 rounded-2xl p-5 hover:border-MyovoltPurple hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-MyovoltPurple/30"
    >
      {/* Top row */}
      <div className="flex items-start justify-between mb-4 gap-2">
        <div className="flex items-center gap-3 min-w-0">
          <InitialAvatar name={group.groupId} />
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 truncate leading-tight">{group.groupId}</p>
            <p className="text-xs text-gray-400 truncate mt-0.5">{group.recoveryPlan || 'No plan'}</p>
          </div>
        </div>
        <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full border font-medium ${meta.badge}`}>
          <span className={`inline-block w-1.5 h-1.5 rounded-full ${meta.dot} mr-1`} />
          {meta.label}
        </span>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-gray-50 rounded-xl p-2.5 text-center">
          <p className="text-lg font-bold text-gray-800 leading-none">{memberCount}</p>
          <p className="text-xs text-gray-400 mt-1">Members</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-2.5 text-center">
          <p className="text-lg font-bold text-MyovoltPurple leading-none">{treatmentCount30d}</p>
          <p className="text-xs text-gray-400 mt-1">Treatments</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-2.5 text-center">
          <p className="text-lg font-bold text-gray-800 leading-none">{hrs > 0 ? `${hrs}h` : `${mins}m`}</p>
          <p className="text-xs text-gray-400 mt-1">Tx time</p>
        </div>
      </div>

      {/* Active member bar */}
      <div className="mb-3">
        <div className="flex justify-between text-xs text-gray-400 mb-1">
          <span>Active members (30d)</span>
          <span className="font-medium text-gray-600">{activeMembers30d}/{memberCount}</span>
        </div>
        <SparkBar
          value={activeMembers30d}
          max={memberCount || 1}
          colour={activityLevel === 'dormant' ? 'bg-gray-300' : 'bg-MyovoltPurple'}
        />
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <p className="text-xs text-gray-400">Last activity: <span className="text-gray-600">{fmtDate(lastActivity)}</span></p>
        <svg className="w-4 h-4 text-gray-300 group-hover:text-MyovoltPurple transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </button>
  );
};

// ─── GroupView modal ──────────────────────────────────────────────────────────

const GroupView: React.FC<{ data: SharedData; onClose: () => void }> = ({ data, onClose }) => {
  const { users, groups } = data;

  const [allStats, setAllStats] = React.useState<GroupStats[]>([]);
  const [loading, setLoading]   = React.useState(true);
  const [expanded, setExpanded] = React.useState<GroupStats | null>(null);
  const [editingGroup, setEditingGroup] = React.useState<Group | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [search, setSearch]     = React.useState('');
  const [sort, setSort]         = React.useState<'alpha' | 'members' | 'treatments' | 'activity'>('treatments');

  // Build a userId → User map once
  const userById = useMemo(() =>
    Object.fromEntries(users.map(u => [u.id, u])),
    [users]
  );

  // Build a userEmail → userId map once
  const userByEmail = useMemo(() =>
    Object.fromEntries(users.map(u => [u.email, u.id])),
    [users]
  );

  useEffect(() => {
    const fetchActivity = async () => {
      setLoading(true);
      try {
        const cutoff = Timestamp.fromDate(THIRTY_DAYS_AGO);
        const snap = await getDocs(
          query(collection(db, 'treatments'), where('startTime', '>=', cutoff))
        );

        // Aggregate per user: count + minutes
        const txByUser: Record<string, { count: number; minutes: number; last: Date }> = {};
        snap.forEach(d => {
          const { userId, length, startTime } = d.data();
          if (!userId) return;
          const date: Date = startTime?.toDate?.() ?? new Date(0);
          if (!txByUser[userId]) txByUser[userId] = { count: 0, minutes: 0, last: new Date(0) };
          txByUser[userId].count++;
          txByUser[userId].minutes += Number(length) || 0;
          if (date > txByUser[userId].last) txByUser[userId].last = date;
        });

        const stats: GroupStats[] = groups.map(group => {
          const memberIds = group.Members
            .map(email => userByEmail[email])
            .filter(Boolean) as string[];

          const members: MemberActivity[] = memberIds.map(uid => {
            const u = userById[uid];
            const tx = txByUser[uid];
            return {
              userId: uid,
              name:   u?.name  ?? 'Unknown',
              email:  u?.email ?? '',
              treatmentCount: tx?.count   ?? 0,
              totalMinutes:   tx?.minutes ?? 0,
              lastSeen:       tx?.last    ?? null,
            };
          });

          const treatmentCount30d  = members.reduce((s, m) => s + m.treatmentCount, 0);
          const totalMinutes30d    = members.reduce((s, m) => s + m.totalMinutes, 0);
          const activeMembers30d   = members.filter(m => m.treatmentCount > 0).length;
          const lastActivity       = members
            .map(m => m.lastSeen)
            .filter(Boolean)
            .reduce<Date | null>((latest, d) => (!latest || (d! > latest) ? d! : latest), null);

          return {
            group,
            memberCount:     memberIds.length,
            treatmentCount30d,
            totalMinutes30d,
            activeMembers30d,
            lastActivity,
            members,
            activityLevel: classifyActivity(treatmentCount30d, memberIds.length),
          };
        });

        setAllStats(stats);
      } catch (err) {
        console.error('GroupView fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchActivity();
  }, [groups, userById, userByEmail]);

  const filtered = useMemo(() => {
    let s = allStats.filter(gs =>
      gs.group.groupId.toLowerCase().includes(search.toLowerCase()) ||
      gs.group.recoveryPlan?.toLowerCase().includes(search.toLowerCase())
    );
    switch (sort) {
      case 'alpha':      s = [...s].sort((a, b) => a.group.groupId.localeCompare(b.group.groupId)); break;
      case 'members':    s = [...s].sort((a, b) => b.memberCount - a.memberCount); break;
      case 'treatments': s = [...s].sort((a, b) => b.treatmentCount30d - a.treatmentCount30d); break;
      case 'activity': {
        const order: Record<GroupStats['activityLevel'], number> = {
          'very-active': 0, 'active': 1, 'quiet': 2, 'dormant': 3, 'new': 4,
        };
        s = [...s].sort((a, b) => order[a.activityLevel] - order[b.activityLevel]);
        break;
      }
    }
    return s;
  }, [allStats, search, sort]);

  // Summary counts
  const summary = useMemo(() => ({
    total:      allStats.length,
    veryActive: allStats.filter(g => g.activityLevel === 'very-active').length,
    active:     allStats.filter(g => g.activityLevel === 'active').length,
    quiet:      allStats.filter(g => g.activityLevel === 'quiet').length,
    dormant:    allStats.filter(g => g.activityLevel === 'dormant').length,
  }), [allStats]);

  return (
    <>
      <ModalShell title="Group Overview" onClose={onClose}>

        {/* ── Top summary bar ── */}
        {!loading && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6 -mt-2">
            {[
              { label: 'Total Groups',  value: summary.total,      colour: 'text-gray-800'    },
              { label: 'Very Active',   value: summary.veryActive,  colour: 'text-emerald-600' },
              { label: 'Active',        value: summary.active,      colour: 'text-green-600'   },
              { label: 'Quiet',         value: summary.quiet,       colour: 'text-amber-600'   },
              { label: 'Dormant',       value: summary.dormant,     colour: 'text-gray-400'    },
            ].map(({ label, value, colour }) => (
              <div key={label} className="bg-gray-50 rounded-2xl px-4 py-3 text-center">
                <p className={`text-2xl font-bold leading-none ${colour}`}>{value}</p>
                <p className="text-xs text-gray-400 mt-1">{label}</p>
              </div>
            ))}
          </div>
        )}

        {/* ── Controls ── */}
        <div className="flex flex-wrap gap-3 mb-5">
          <div className="relative flex-1 min-w-[200px]">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search groups, plans…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="input input-bordered w-full pl-9"
            />
          </div>
          <select
            className="select select-bordered"
            value={sort}
            onChange={e => setSort(e.target.value as typeof sort)}
          >
            <option value="treatments">Sort: Most Treatments</option>
            <option value="members">Sort: Most Members</option>
            <option value="activity">Sort: Activity Level</option>
            <option value="alpha">Sort: A → Z</option>
          </select>
        </div>

        {/* ── Grid ── */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <span className="loading loading-spinner loading-lg text-MyovoltPurple" />
            <p className="text-sm text-gray-400">Loading activity data…</p>
          </div>
        ) : (
          <>
            {filtered.length === 0 && search && (
              <p className="text-center text-gray-400 py-6">No groups match your search.</p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {filtered.map(stats => (
                <GroupCard key={stats.group.id} stats={stats} onClick={() => setExpanded(stats)} />
              ))}
              <button
                onClick={() => setCreating(true)}
                className="flex flex-col items-center justify-center gap-2 min-h-[180px] border-2 border-dashed border-gray-300 rounded-2xl p-5 text-gray-400 hover:border-MyovoltPurple hover:text-MyovoltPurple transition-colors focus:outline-none focus:ring-2 focus:ring-MyovoltPurple/30"
              >
                <span className="text-3xl leading-none font-light">+</span>
                <span className="text-sm font-medium">New Group</span>
              </button>
            </div>
          </>
        )}
      </ModalShell>

      {expanded && (
        <GroupDetailPanel
          stats={expanded}
          onClose={() => setExpanded(null)}
          onModify={() => { setEditingGroup(expanded.group); setExpanded(null); }}
        />
      )}

      {editingGroup && (
        <ModifyGroupAdmin data={data} group={editingGroup} onClose={() => setEditingGroup(null)} />
      )}

      {creating && (
        <CreateGroupAdmin data={data} onClose={() => setCreating(false)} />
      )}
    </>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// TOP-LEVEL ORCHESTRATOR
// ═════════════════════════════════════════════════════════════════════════════

interface GroupsProps {
  isGroupMember: boolean;
  groupId: string | null | undefined;
}

const Groups: React.FC<GroupsProps> = ({ isGroupMember, groupId }) => {
  const { currentUserLevel } = useUserLevel();
  const [isOpen, setIsOpen] = useState(false);

  // ── Single shared data fetch ──────────────────────────────────────────────
  const [data, setData] = useState<SharedData>({
    users: [],
    groups: [],
    recoveryPlans: [],
    sequenceCounts: { ...DEFAULT_SEQUENCE_COUNTS },
    availableForms: [],
  });
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [usersSnap, plansSnap, groupsSnap, seqSnap, formsStructuresSnap] = await Promise.all([
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'recovery_plans')),
          getDocs(collection(db, 'groups')),
          getDocs(collection(db, 'sequences')),
          getDoc(doc(db, 'forms', 'structures')),
        ]);

        const users: User[] = usersSnap.docs.map(d => ({
          id: d.id,
          name: d.data().name,
          email: d.data().email,
          groupId: d.data().groupId || null,
          access: d.data().access || DEFAULT_FEATURES,
          userSequences: d.data().userSequences,
        }));

        const recoveryPlans: RecoveryPlan[] = plansSnap.docs.map(d => ({
          id: d.id,
          name: d.data().name ?? d.id,
        }));

        const groups: Group[] = groupsSnap.docs.map(d => ({
          id: d.id,
          groupId: d.data().groupId,
          recoveryPlan: d.data().recoveryPlan,
          Members: d.data().Members || [],
          password: d.data().password || '',
          access: d.data().access || DEFAULT_FEATURES,
          permissions: readGroupPermissions(d.data()),
          backMovements: d.data().backAssessmentMovements ?? ALL_BACK_MOVEMENTS,
          sequences: d.data().sequences ?? DEFAULT_PRODUCT_INDEXES,
          forms: d.data().forms ?? [],
        }));

        const sequenceCounts = { ...DEFAULT_SEQUENCE_COUNTS };
        seqSnap.forEach(d => {
          if (PRODUCT_CODES.includes(d.id as ProductCode)) {
            sequenceCounts[d.id as ProductCode] =
              Object.keys(d.data()).filter(k => k.startsWith('sequence')).length;
          }
        });

        const structuresData = formsStructuresSnap.exists()
          ? (formsStructuresSnap.data() as Record<string, { name: string }>)
          : {};
        const availableForms: FormOption[] = Object.entries(structuresData)
          .map(([id, s]) => ({ id: Number(id), name: s.name }))
          .sort((a, b) => a.id - b.id);

        setData({ users, groups, recoveryPlans, sequenceCounts, availableForms });
        setDataLoaded(true);
      } catch (err) {
        console.error('Error fetching group data:', err);
      }
    };

    fetchAll();
  }, []);

  const canOpen =
    currentUserLevel === AdminLevel.SUPER_ADMIN ||
    (currentUserLevel === AdminLevel.USER && isGroupMember);

  if (!canOpen) return null;

  const handleClose = () => setIsOpen(false);

  const renderModal = (): React.ReactNode => {
    if (!isOpen || !dataLoaded) return null;
    if (currentUserLevel === AdminLevel.SUPER_ADMIN) {
      return <GroupView data={data} onClose={handleClose} />;
    }
    return groupId
      ? <ViewMyGroup data={data} groupId={groupId} onClose={handleClose} />
      : null;
  };

  return (
    <>
      <button className="btn mx-2 btn-inactive" onClick={() => setIsOpen(true)}>Groups</button>
      {renderModal()}
    </>
  );
};

export default Groups;